import type { FocusEventHandler } from 'react';
import { styled } from '@mui/material/styles';
import {
  Control,
  FieldValues,
  DeepMap,
  FieldError,
  Controller,
} from 'react-hook-form';
import { Box, FormControl } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import { PHONE_VALIDATION_COUNTRIES } from '@/data/constants';

import TextInput from './TextInput';

const PREFIX = 'PhoneInput';

const classes = {
  phone: `${PREFIX}-phone`,
  country: `${PREFIX}-country`,
  number: `${PREFIX}-number`,
  extension: `${PREFIX}-extension`,
};

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  [`& .${classes.phone}`]: {
    display: 'flex',
    gap: theme.spacing(1),
  },

  [`& .${classes.country}`]: {
    width: '35%',
  },

  [`& .${classes.number}`]: {
    flexGrow: 1,
  },

  [`& .${classes.extension}`]: {
    width: 80,
    // Hide number input arrows
    '& input[type="number"]': {
      MozAppearance: 'textfield',
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
    },
  },
  ['.MuiTelInput-IconButton']: {
    paddingTop: 0,
    marginTop: 12,
  },
  [`.MuiTelInput-Flag`]: {
    marginTop: 12,
  },
}));

const US_CALLING_CODE = '+1';

const enforceUSCallingCodePrefix = (value?: string | null): string => {
  const raw = value ?? '';
  const digits = raw.replace(/\D/g, '');

  if (!digits) {
    return US_CALLING_CODE;
  }

  let normalized = digits;
  if (!normalized.startsWith('1')) {
    normalized = `1${normalized}`;
  } else {
    normalized = `1${normalized.slice(1)}`;
  }

  return `+${normalized}`;
};

interface PhoneInputProps {
  label: string;
  name: string;
  className?: string;
  extensionName?: string;
  control: Control;
  required?: boolean;
  disableDropdown?: boolean;
  errors?: DeepMap<FieldValues, FieldError>;
  defaultValue?: any;
  formHook?: any;
  readOnly?: boolean;
  onValueChange?: (value: string) => void;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export default function PhoneInput({
  label,
  name,
  className,
  extensionName,
  control,
  required = false,
  disableDropdown = true,
  errors = undefined,
  defaultValue = undefined,
  readOnly = false,
  onValueChange,
  onBlur: onBlurProp,
}: PhoneInputProps) {
  const { t } = useTranslation('common');

  return (
    <StyledFormControl className={className}>
      <Box component="div" className={classes.phone}>
        <Controller
          name={name}
          control={control}
          rules={{
            validate: (value) => {
              const isValid = matchIsValidTel(value, {
                onlyCountries: PHONE_VALIDATION_COUNTRIES,
              });
              const isEmpty = !value || value === '+1';
              return required ? isValid : isValid || isEmpty;
            },
          }}
          render={({
            field: { ref: fieldRef, value, onChange, onBlur, name: fieldName },
            fieldState,
          }) => {
            const maskedValue = enforceUSCallingCodePrefix(
              value as string | null,
            );

            return (
              <MuiTelInput
                name={fieldName}
                required={required}
                value={maskedValue}
                onChange={(newValue) => {
                  const nextValue = enforceUSCallingCodePrefix(newValue);
                  onChange(nextValue);
                  if (onValueChange) {
                    onValueChange(nextValue);
                  }
                }}
                onBlur={(event) => {
                  onBlur();
                  onBlurProp?.(event);
                }}
                inputRef={fieldRef}
                onlyCountries={PHONE_VALIDATION_COUNTRIES}
                helperText={fieldState.invalid ? t('common.invalidPhone') : ''}
                error={fieldState.invalid}
                disableDropdown={disableDropdown}
                defaultCountry="US"
                className={
                  disableDropdown ? 'MuiTelInput--disableDropdown' : ''
                }
                label={label}
                id={name}
                InputProps={{
                  readOnly,
                  sx: {
                    '& input': {
                      cursor: readOnly ? 'default' : 'inherit',
                    },
                  },
                }}
              />
            );
          }}
        />
        {extensionName && (
          <TextInput
            label={t('common.extension')}
            type="number"
            name={extensionName}
            required={false}
            control={control}
            errors={errors}
            defaultValue={defaultValue}
            className={classes.extension}
            inputProps={{ maxLength: 10, min: 0 }}
          />
        )}
      </Box>
    </StyledFormControl>
  );
}
